import { PrivateRoute } from 'components/route'
import { isSameDay, parseISO } from 'date-fns'
import { useMedicoesAtendimentosAnterioresQuery, useMedicoesCardQuery } from 'graphql/hooks.generated'
import { SexoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { MetaArray } from 'util/metaPath'
import { useIsCriancaPreTermo } from 'view/atendimentos/components/hooks/useIsCriancaPreTermo'
import { AntropometriaAccordionModel } from 'view/atendimentos/components/MedicoesPanel/AntropometriaAccordion'
import { SinaisVitaisAccordionModel } from 'view/atendimentos/components/MedicoesPanel/SinaisVitaisAccordion'
import {
  convertMedicaoAnterior,
  convertMedicaoAtendimentoAtual,
  convertMedicaoOutrosAtendimentos,
  convertMedicoesNoNascimento,
} from 'view/atendimentos/detail/components/modals/medicoes/converter-medicoes'
import { HistoricoMedicoesModal } from 'view/atendimentos/detail/components/modals/medicoes/HistoricoMedicoesModal'
import { MedicaoAnteriorModel } from 'view/atendimentos/detail/components/modals/medicoes/model-medicoes'
import { MedicoesPreNatalAtendimentoModel } from 'view/atendimentos/detail/components/modals/types/model-historicoPreNatal'
import { PeriodoGestacaoModel } from 'view/atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { grupoCboMedicoes } from '../../objetivo/acessos'
import { TipoPreNatal } from '../../pre-natal/model-preNatal'
import { ProntuarioCard } from '../ProntuarioCard'
import { concatenarOrigemTagAnalytics } from '../util/analyticsUtils'
import { convertInputToModelAndFormat } from './convertInputToModelAndFormat'
import { MedicoesCardReadOnly } from './MedicoesCardReadOnly'
import { MedicoesCardSoapPage } from './MedicoesCardSoapPage'
import { mergeMedicoesNoNascimento } from './mergeMedicoesNoNascimento'
import { CidadaoMedicoes } from './model'

export interface MedicoesSectionProps extends MedicoesPreNatalAtendimentoModel {
  fieldName?: MetaArray<MedicaoAnteriorModel>
  prontuarioId: ID
  dataAtendimento?: Instant
  cidadao: CidadaoMedicoes
  sinaisVitaisAtual?: SinaisVitaisAccordionModel
  antropometriaAtual?: AntropometriaAccordionModel
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isGestante: boolean
  isAtendimentoObservacao: boolean
  isAtendimentoOdonto: boolean
  isAtendimentoProcedimentos: boolean
  tipoPreNatal?: TipoPreNatal
  isRetificacao?: boolean
  readOnly?: boolean
  medicoesAnterioresAtendimentoAtual?: MedicaoAnteriorModel[]
}

export function MedicoesSection(props: Readonly<MedicoesSectionProps>) {
  const {
    prontuarioId,
    dataAtendimento,
    isGestante,
    gestacoes,
    cidadao,
    isAtendimentoObservacao,
    tipoPreNatal,
    readOnly,
    isRetificacao = false,
    isAtendimentoOdonto,
    isAtendimentoProcedimentos,
    medicoesAnterioresAtendimentoAtual,
  } = props

  const { analytics } = useFirebase()
  const history = useHistory()
  const { getServerTimeNow } = useServerTime()
  const match = useRouteMatch()
  const modalUrl = `${match.url}/historicoMedicoes`

  const handleClick = () => {
    history.push(modalUrl)
    const tag = concatenarOrigemTagAnalytics('acessar_card_medicoes', match.url)
    analytics.logEvent(tag)
  }

  const [canRender, setCanRender] = useState(false)

  const { data: medicoesCard, loading: loadingMedicoesCard } = useMedicoesCardQuery({
    variables: { prontuarioId: prontuarioId, fetchMedicoesAnteriores: !readOnly, dataReferencia: dataAtendimento },
    skip: isRetificacao,
  })

  const { data: medicoesAnteriores, loading: loadingMedicoesAnteriores } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    skip: isRetificacao,
  })

  const historicoMedicoes: HistoricoMedicaoModel[] = useMemo(() => {
    return [
      ...[...(medicoesAnteriores?.medicoes ?? [])]
        .sort((a, b) => b.dataMedicao - a.dataMedicao)
        .map(convertMedicaoOutrosAtendimentos),

      ...(props.sinaisVitaisAtual && props.antropometriaAtual
        ? [convertMedicaoAtendimentoAtual(props.sinaisVitaisAtual, props.antropometriaAtual, dataAtendimento)]
        : []),

      ...(medicoesAnteriores?.medicoesNoNascimento
        ? [convertMedicoesNoNascimento(medicoesAnteriores.medicoesNoNascimento, cidadao.dataNascimento)]
        : []),
    ]
  }, [medicoesAnteriores, props.sinaisVitaisAtual, props.antropometriaAtual, dataAtendimento, cidadao.dataNascimento])

  const historicoMedicoesAtendimentoAtual: HistoricoMedicaoModel[] = useMemo(() => {
    return [
      ...historicoMedicoes,

      ...[...(medicoesAnterioresAtendimentoAtual ?? [])]
        .sort((a, b) => Number(b.data) - Number(a.data))
        .map(convertMedicaoAnterior),
    ]
  }, [historicoMedicoes, medicoesAnterioresAtendimentoAtual])

  const nascimentoCidadao = Number(parseISO(cidadao.dataNascimento))
  const cidadaoNasceuHoje = isSameDay(nascimentoCidadao, getServerTimeNow())
  const ultimasMedicoesDoDia = useMemo(
    () =>
      cidadaoNasceuHoje
        ? mergeMedicoesNoNascimento(
            medicoesCard?.ultimasMedicoesDoDia,
            medicoesCard?.medicoesNoNascimento,
            nascimentoCidadao
          )
        : medicoesCard?.ultimasMedicoesDoDia,
    [cidadaoNasceuHoje, medicoesCard?.medicoesNoNascimento, medicoesCard?.ultimasMedicoesDoDia, nascimentoCidadao]
  )
  const ultimasMedicoesDiasAnteriores = useMemo(
    () =>
      !cidadaoNasceuHoje && !readOnly
        ? mergeMedicoesNoNascimento(
            medicoesCard?.ultimasMedicoesDiasAnteriores,
            medicoesCard?.medicoesNoNascimento,
            nascimentoCidadao
          )
        : medicoesCard?.ultimasMedicoesDiasAnteriores,
    [
      cidadaoNasceuHoje,
      medicoesCard?.medicoesNoNascimento,
      medicoesCard?.ultimasMedicoesDiasAnteriores,
      nascimentoCidadao,
      readOnly,
    ]
  )
  const { medicoesHoje, medicoesAnterioresExpandido, medicoesAnterioresAccordion } = useMemo(
    () => convertInputToModelAndFormat(ultimasMedicoesDoDia, ultimasMedicoesDiasAnteriores),
    [ultimasMedicoesDiasAnteriores, ultimasMedicoesDoDia]
  )

  const getTooltipMessage = () => {
    if (readOnly) {
      return medicoesHoje.length === 0 ? 'Nenhuma medição registrada hoje' : 'Ver medições'
    }
    if (canRender) {
      return 'Ver ou adicionar medições '
    }
    return 'Adicionar medições anteriores'
  }

  const tooltipMessage = getTooltipMessage()

  useEffect(() => {
    setCanRender(medicoesHoje.length !== 0 || medicoesAnterioresExpandido.length !== 0)
  }, [medicoesHoje, medicoesAnterioresExpandido])

  const isSexoBinario = cidadao.sexo === SexoEnum.MASCULINO || cidadao.sexo === SexoEnum.FEMININO

  const { isPreTermoNaFaixa64Semanas } = useIsCriancaPreTermo({
    prontuarioId,
    dataNascimento: cidadao.dataNascimento,
    isSexoBinario,
  })

  return (
    <>
      <PrivateRoute
        permission={grupoCboMedicoes.visualizar}
        path={modalUrl}
        render={(renderProps) =>
          readOnly ? (
            <HistoricoMedicoesModal
              {...renderProps}
              cidadao={cidadao}
              prontuarioId={prontuarioId}
              isGestante={isGestante}
              gestacoes={gestacoes}
              isAtendimentoObservacao={isAtendimentoObservacao}
              dataAtendimento={dataAtendimento}
              loadingMedicoes={loadingMedicoesAnteriores}
              medicoes={historicoMedicoes}
              isAtendimentoOdonto={isAtendimentoOdonto}
              isAtendimentoProcedimentos={isAtendimentoProcedimentos}
            />
          ) : (
            <HistoricoMedicoesModal
              {...renderProps}
              cidadao={cidadao}
              prontuarioId={prontuarioId}
              isGestante={isGestante}
              gestacoes={gestacoes}
              dataAtendimento={dataAtendimento}
              fieldName={props.fieldName}
              loadingMedicoes={loadingMedicoesAnteriores}
              medicoes={historicoMedicoes}
              preNatalAtendimentoAtual={props.preNatalAtendimentoAtual}
              dumAtendimentoAtual={props.dumAtendimentoAtual}
              resultadosExamesAtendimentoAtual={props.resultadosExamesAtendimentoAtual}
              isAtendimentoObservacao={isAtendimentoObservacao}
              tipoPreNatal={tipoPreNatal}
              isRetificacao={isRetificacao}
              editable
              isAtendimentoOdonto={isAtendimentoOdonto}
              isAtendimentoProcedimentos={isAtendimentoProcedimentos}
            />
          )
        }
      />
      <ProntuarioCard
        title='Medições'
        tooltip={tooltipMessage}
        onClick={handleClick}
        dataTestId='ProntuarioCard.Medicoes'
      >
        {readOnly ? (
          <MedicoesCardReadOnly
            loading={loadingMedicoesCard || loadingMedicoesAnteriores}
            prontuarioId={prontuarioId}
            dataAtendimento={dataAtendimento}
            historicoMedicoes={historicoMedicoes}
            medicoesHoje={medicoesHoje}
            canRender={canRender}
            cidadao={cidadao}
            gestacoes={gestacoes}
            isGestante={isGestante}
            dumAtendimentoAtual={props.dumAtendimentoAtual}
            tipoPreNatal={tipoPreNatal}
            isPreTermo={isPreTermoNaFaixa64Semanas}
          />
        ) : (
          <MedicoesCardSoapPage
            loading={loadingMedicoesCard || loadingMedicoesAnteriores}
            prontuarioId={prontuarioId}
            dataAtendimento={dataAtendimento}
            historicoMedicoes={historicoMedicoesAtendimentoAtual}
            medicoesHoje={medicoesHoje}
            medicoesAnterioresExpandido={medicoesAnterioresExpandido}
            medicoesAnterioresAccordion={medicoesAnterioresAccordion}
            canRender={canRender}
            cidadao={cidadao}
            gestacoes={gestacoes}
            isGestante={isGestante}
            dumAtendimentoAtual={props.dumAtendimentoAtual}
            tipoPreNatal={tipoPreNatal}
            isPreTermo={isPreTermoNaFaixa64Semanas}
          />
        )}
      </ProntuarioCard>
    </>
  )
}
